import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Users, FileText, Upload, ArrowRight, Calendar, Archive, Mail, X, ArchiveRestore } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const VIEW = { ACTIVE: "active", ARCHIVED: "archived" };

export default function Dashboard() {
  const [patients, setPatients] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(VIEW.ACTIVE);

  const isArchived = view === VIEW.ARCHIVED;

  const reload = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/patients"),
        api.get("/reports", { params: { archived: isArchived } }),
      ]);
      setPatients(p.data);
      setReports(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setSelected(new Set()); // clear selection when switching views
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const archiveSelected = async () => {
    if (!window.confirm(`Archive ${selected.size} report(s)?`)) return;
    setBusy(true);
    try {
      await api.post("/reports/bulk-archive", { ids: Array.from(selected), archived: true });
      toast.success("Archived");
      clearSelection();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setBusy(false); }
  };

  const restoreSelected = async () => {
    setBusy(true);
    try {
      await api.post("/reports/bulk-archive", { ids: Array.from(selected), archived: false });
      toast.success(`Restored ${selected.size} report${selected.size === 1 ? "" : "s"}`);
      clearSelection();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  const restoreOne = async (reportId) => {
    setBusy(true);
    try {
      await api.post("/reports/bulk-archive", { ids: [reportId], archived: false });
      toast.success("Report restored");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  const emailSelected = async () => {
    const fallback = window.prompt("Optional fallback email if a patient has no email on file (leave blank to skip those):");
    setBusy(true);
    try {
      const { data } = await api.post("/reports/bulk-email", {
        report_ids: Array.from(selected),
        language: "en",
        frontend_origin: window.location.origin,
        fallback_email: fallback || null,
      });
      const failed = data.total - data.sent;
      if (data.sent) toast.success(`Sent ${data.sent} of ${data.total} reports`);
      if (failed) toast.error(`${failed} did not send — see report log`);
      clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk email failed");
    } finally { setBusy(false); }
  };

  const stats = [
    { label: "Patients", value: patients.length, icon: Users },
    { label: "Reports generated", value: reports.length, icon: FileText },
    { label: "This week", value: reports.filter(r => (Date.now() - new Date(r.created_at).getTime()) < 7*24*3600*1000).length, icon: Calendar },
  ];

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-2">Your workspace</h1>
        </div>
        <Link data-testid="dashboard-new-report-cta" to="/upload" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Upload className="w-4 h-4" /> Upload new report
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white border rounded-lg p-6" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>{label}</span>
              <Icon className="w-4 h-4" style={{ color: "var(--color-secondary)" }} />
            </div>
            <div className="font-display text-4xl font-semibold mt-3" style={{ color: "var(--color-text-primary)" }}>{value}</div>
          </div>
        ))}
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="sticky top-3 z-10 mb-6 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-sm" style={{ borderColor: "var(--color-primary)", background: "var(--bg-surface)" }}>
          <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>{selected.size} selected</span>
          {!isArchived && (
            <>
              <button data-testid="bulk-email-button" onClick={emailSelected} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-sm font-medium disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
                <Mail className="w-3.5 h-3.5" /> Send to patients
              </button>
              <button data-testid="bulk-archive-button" onClick={archiveSelected} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium hover:bg-[#F9F8F6] disabled:opacity-60" style={{ borderColor: "var(--color-border)" }}>
                <Archive className="w-3.5 h-3.5" /> Archive
              </button>
            </>
          )}
          {isArchived && (
            <button data-testid="bulk-restore-button" onClick={restoreSelected} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-sm font-medium disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
              <ArchiveRestore className="w-3.5 h-3.5" /> Restore
            </button>
          )}
          <button data-testid="bulk-clear-button" onClick={clearSelection} className="ml-auto p-1.5 rounded-md hover:bg-[#F9F8F6]">
            <X className="w-4 h-4" style={{ color: "var(--color-text-secondary)" }} />
          </button>
        </div>
      )}

      <div className="bg-white border rounded-lg" style={{ borderColor: "var(--color-border)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b" style={{ borderColor: "var(--color-border)" }}>
          <div className="flex items-center gap-3">
            <h2 className="font-display text-xl font-semibold">{isArchived ? "Archived reports" : "Recent reports"}</h2>
            <ViewToggle value={view} onChange={setView} testidPrefix="dashboard" />
          </div>
          <Link data-testid="dashboard-view-all-patients" to="/patients" className="text-sm font-medium inline-flex items-center gap-1" style={{ color: "var(--color-primary)" }}>
            All patients <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {loading ? (
          <div className="p-10 text-center"><div className="spinner mx-auto" /></div>
        ) : reports.length === 0 ? (
          <div className="p-10 text-center">
            {isArchived ? (
              <>
                <Archive className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
                <p className="font-display text-lg">No archived reports</p>
                <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>Reports you archive will appear here. You can restore them anytime.</p>
              </>
            ) : (
              <>
                <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
                <p className="font-display text-lg">No reports yet</p>
                <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>Upload your first imaging report to get started.</p>
                <Link data-testid="dashboard-empty-cta" to="/upload" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
                  <Upload className="w-4 h-4" /> Upload a report
                </Link>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
            {reports.slice(0, 12).map((r) => {
              const checked = selected.has(r.report_id);
              return (
                <div key={r.report_id} className={`flex items-center gap-3 px-6 py-4 transition-colors ${checked ? "bg-[#F9F8F6]" : "hover:bg-[#F9F8F6]"}`}>
                  <input
                    data-testid={`select-report-${r.report_id}`}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(r.report_id)}
                    className="w-4 h-4 accent-[#2A4736] cursor-pointer"
                  />
                  <Link
                    data-testid={`dashboard-report-row-${r.report_id}`}
                    to={`/reports/${r.report_id}`}
                    className="flex-1 flex items-center justify-between min-w-0"
                  >
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2 truncate">
                        {r.patient_name}
                        {r.status && r.status !== "ready" && r.status !== "failed" && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--bg-canvas)", color: "var(--color-text-secondary)" }}>Processing</span>
                        )}
                        {r.status === "failed" && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#FCE8E8", color: "var(--color-error)" }}>Failed</span>
                        )}
                        {isArchived && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--bg-canvas)", color: "var(--color-text-secondary)" }}>Archived</span>
                        )}
                      </div>
                      <div className="text-xs mt-0.5 truncate" style={{ color: "var(--color-text-secondary)" }}>
                        {r.modality} · {r.original_filename}
                        {isArchived && r.archived_at && <> · archived {format(new Date(r.archived_at), "d MMM yyyy")}</>}
                      </div>
                    </div>
                    <div className="text-xs ml-4 flex-shrink-0" style={{ color: "var(--color-text-secondary)" }}>
                      {format(new Date(r.created_at), "d MMM yyyy")}
                    </div>
                  </Link>
                  {isArchived && (
                    <button
                      data-testid={`restore-report-${r.report_id}`}
                      onClick={() => restoreOne(r.report_id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium hover:bg-white disabled:opacity-60"
                      style={{ borderColor: "var(--color-border)" }}
                      title="Restore this report"
                    >
                      <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

function ViewToggle({ value, onChange, testidPrefix }) {
  const base = "px-3 py-1 text-xs font-medium rounded-md transition";
  return (
    <div className="inline-flex items-center p-0.5 border rounded-lg" style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)" }}>
      <button
        data-testid={`${testidPrefix}-view-active`}
        onClick={() => onChange(VIEW.ACTIVE)}
        className={base}
        style={{
          background: value === VIEW.ACTIVE ? "var(--bg-surface)" : "transparent",
          color: value === VIEW.ACTIVE ? "var(--color-text-primary)" : "var(--color-text-secondary)",
          boxShadow: value === VIEW.ACTIVE ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        }}
      >
        Active
      </button>
      <button
        data-testid={`${testidPrefix}-view-archived`}
        onClick={() => onChange(VIEW.ARCHIVED)}
        className={base}
        style={{
          background: value === VIEW.ARCHIVED ? "var(--bg-surface)" : "transparent",
          color: value === VIEW.ARCHIVED ? "var(--color-text-primary)" : "var(--color-text-secondary)",
          boxShadow: value === VIEW.ARCHIVED ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        }}
      >
        Archived
      </button>
    </div>
  );
}
